import {
  BaseModal,
  CommonBinaryModalContent,
} from '@/components/ui/common/modal'
import { IAiConversation, IAiConversationBase } from '@/types/firebase'
import React, { Dispatch, FC, SetStateAction, useState } from 'react'
import { twJoin } from 'tailwind-merge'
import { AiAssistantRenameConversationModalContent } from './AiAssistantRenameConversationModalContent'
import { AiAssistantSidebarItemOptionBtn } from './AiAssistantSidebarItemOptionBtn'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid'
import { cleanConversationTitle } from '@/utils/topic-name-cleaner'

interface Props
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  conversation: IAiConversation
  handleSidebarItemClick: () => void
  handleRenameConversation: (args: {
    conversationId: string
    updateObj: Partial<IAiConversationBase>
    isLoadingHandler: Dispatch<SetStateAction<boolean>>
    onSuccessHandler: () => void
  }) => Promise<void>
  handleDeleteConversation: (args: {
    conversationId: string
    isLoadingHandler: Dispatch<SetStateAction<boolean>>
    onSuccessHandler: () => void
  }) => Promise<void>
  isHovered?: boolean
  isCurrent?: boolean
}

const AiAssistantSidebarItem: FC<Props> = ({
  conversation,
  handleSidebarItemClick,
  className = 'w-full h-8 flex items-center gap-2',
  handleRenameConversation,
  handleDeleteConversation,
  isHovered,
  isCurrent,
  ...props
}) => {
  const [showRenameConversationModal, setShowRenameConversationModal] =
    useState(false)
  const [isRenameLoading, setIsRenameLoading] = useState(false)
  const [showDeleteConversationModal, setShowDeleteConversationModal] =
    useState(false)
  const [isDeleteLoading, setIsDeleteLoading] = useState(false)

  return (
    <div id={conversation.id} className={className} {...props}>
      <button
        onClick={handleSidebarItemClick}
        className={twJoin(
          'w-full h-full px-2 py-1 text-v8-mut text-start text-nowrap text-ellipsis font-medium rounded-md overflow-hidden',
          isHovered && 'bg-v8-line',
          isCurrent && 'bg-v8-line text-v8-blue pointer-events-none',
        )}
      >
        {cleanConversationTitle(conversation.title)}
      </button>

      {isHovered && (
        <>
          <AiAssistantSidebarItemOptionBtn
            onClick={() => setShowRenameConversationModal(true)}
            tooltipId={`${conversation.id}-edit-conversation-title`}
            tooltipText={'Change Title'}
            className={
              'text-v8-mut bg-v8-line hover:text-white hover:bg-v8-blue'
            }
          >
            <PencilIcon className="w-4 h-4" />
          </AiAssistantSidebarItemOptionBtn>

          <AiAssistantSidebarItemOptionBtn
            onClick={() => setShowDeleteConversationModal(true)}
            tooltipId={`${conversation.id}-delete-conversation`}
            tooltipText={'Delete Chat'}
            className={`text-v8-mut bg-v8-line hover:text-white hover:bg-v8-violet`}
          >
            <TrashIcon className="w-4 h-4" />
          </AiAssistantSidebarItemOptionBtn>
        </>
      )}

      <BaseModal
        isOpen={showRenameConversationModal}
        onClose={() => setShowRenameConversationModal(false)}
        customZIndexClass={'z-[665]'}
        bodyAdditionalClassName="max-sm:w-full"
        content={
          <AiAssistantRenameConversationModalContent
            conversation={conversation}
            handleRenameConversation={async ({ title }) => {
              await handleRenameConversation({
                conversationId: conversation.id,
                updateObj: { title },
                isLoadingHandler: setIsRenameLoading,
                onSuccessHandler: () => setShowRenameConversationModal(false),
              })
            }}
            isLoading={isRenameLoading}
            className="w-full pt-4 pb-2 sm:max-w-[400px] sm:min-w-[400px] flex flex-col items-center gap-8"
          />
        }
      />

      <BaseModal
        isOpen={showDeleteConversationModal}
        onClose={() => setShowDeleteConversationModal(false)}
        customZIndexClass={'z-[665]'}
        content={
          <CommonBinaryModalContent
            isLoading={isDeleteLoading}
            title="Confirm Chat Removal"
            subtitle={`Are you sure you want to delete "${cleanConversationTitle(
              conversation.title,
            )}" Chat? This action cannot be undone.`}
            className="w-full pt-4 pb-2 sm:max-w-[400px] sm:min-w-[400px] flex flex-col items-center gap-8"
            leftBtnProps={{
              id: 'yes',
              text: 'Yes',
              icon: null,
              onClickHandler: () =>
                handleDeleteConversation({
                  conversationId: conversation.id,
                  isLoadingHandler: setIsDeleteLoading,
                  onSuccessHandler: () => setShowDeleteConversationModal(false),
                }),
              additionalStyles: 'min-w-[60px]',
            }}
            rightBtnProps={{
              id: 'no',
              text: 'No',
              icon: null,
              onClickHandler: () => setShowDeleteConversationModal(false),
              additionalStyles: 'min-w-[60px]',
            }}
          />
        }
      />
    </div>
  )
}

export default AiAssistantSidebarItem
